import { TextInput, Label } from '@c2fo/liquidity';
import { ScheduleType } from '../../api/shared';
import { CronEditor } from './CronEditor';
import { RRuleEditor } from './RRuleEditor';

interface ScheduleEditorProps {
  scheduleType: ScheduleType;
  expression: string;
  timezone: string;
  onTypeChange: (type: ScheduleType) => void;
  onExpressionChange: (expression: string) => void;
  onTimezoneChange: (timezone: string) => void;
}

export const ScheduleEditor: React.FC<ScheduleEditorProps> = ({
  scheduleType,
  expression,
  timezone,
  onTypeChange,
  onExpressionChange,
  onTimezoneChange,
}) => {
  return (
    <div className="space-y-4">
      <div>
        <Label className="block text-sm font-medium text-gray-700 mb-2">
          Schedule Type <span className="text-red-500">*</span>
        </Label>
        <div className="flex gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="scheduleType"
              value={ScheduleType.SCHEDULE_TYPE_CRON}
              checked={scheduleType === ScheduleType.SCHEDULE_TYPE_CRON}
              onChange={() => {
                onExpressionChange('');
                onTypeChange(ScheduleType.SCHEDULE_TYPE_CRON);
              }}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
            />
            <span className="text-sm text-gray-700">Cron Schedule</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="scheduleType"
              value={ScheduleType.SCHEDULE_TYPE_RATE}
              checked={scheduleType === ScheduleType.SCHEDULE_TYPE_RATE}
              onChange={() => {
                onExpressionChange('');
                onTypeChange(ScheduleType.SCHEDULE_TYPE_RATE);
              }}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
            />
            <span className="text-sm text-gray-700">RRULE (Recurrence Rule)</span>
          </label>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          {scheduleType === ScheduleType.SCHEDULE_TYPE_CRON
            ? 'Run at specific times using cron syntax (e.g., daily at 9 AM, every Monday)'
            : 'Run using RFC 5545 recurrence rules (e.g., every 2 hours, weekly on Mon/Wed/Fri)'}
        </p>
      </div>

      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        {scheduleType === ScheduleType.SCHEDULE_TYPE_CRON ? (
          <CronEditor value={expression} onChange={onExpressionChange} />
        ) : (
          <RRuleEditor value={expression} onChange={onExpressionChange} />
        )}
      </div>

      <div>
        <Label className="block text-sm font-medium text-gray-700 mb-1">Timezone</Label>
        <TextInput
          value={timezone}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onTimezoneChange(e.target.value)}
          placeholder="UTC"
          className="w-full"
          size="sm"
        />
        <p className="mt-1 text-xs text-gray-500">IANA timezone name (e.g., America/New_York, Europe/London)</p>
      </div>
    </div>
  );
};
